import React from 'react';
import { Alert, Icon, Link } from 'pounceV2';
import urls from 'Source/urls';
import { LinkButtonV3 } from 'Components/buttons/LinkButton';
import useIsCreatePROnSaveEnabled from 'Hooks/useIsCreatePROnSaveEnabled';
import { RuleFilterSidesheetValues } from './RuleFilterSidesheetV3';

export type RuleFilterSidesheetFormAlertProps = {
  ruleDetails: Omit<RuleFilterSidesheetValues, 'alertEventValues' | 'addTest'>;
  isValid?: boolean;
  error?: string;
  isSuccess?: boolean;
  addTest?: boolean;
  additionalSuccessMessageData?: Record<string, any>;
};

type AlertTitleProps = Omit<RuleFilterSidesheetFormAlertProps, 'isSuccess'>;

const AlertTitle = ({
  ruleDetails,
  isValid,
  error,
  addTest,
  additionalSuccessMessageData,
}: AlertTitleProps) => {
  const { isEnabled: isCreatePROnSaveEnabled } = useIsCreatePROnSaveEnabled();

  if (isCreatePROnSaveEnabled) {
    return error || !isValid ? (
      `${
        !isValid
          ? 'The required fields below should be filled'
          : 'There were errors trying to create a PR with additional filter(s)'
      }. The PR has not been created.`
    ) : (
      <>
        A PR with filter
        {addTest && (
          <>
            {' '}
            and unit test &quot;<b>{ruleDetails.tests[ruleDetails.tests.length - 1].name}</b>
            &quot;
          </>
        )}{' '}
        changes has been successfully created.
        <br />
        {additionalSuccessMessageData?.prUrl && (
          <Link href={additionalSuccessMessageData.prUrl} size="small" external>
            View PR on GitHub <Icon type="external-link" size="small" />
          </Link>
        )}
      </>
    );
  }

  return error || !isValid ? (
    `${
      !isValid
        ? 'The required fields below should be filled'
        : 'There were errors trying to save the rule with additional filter(s)'
    }. Changes have not been saved to your detection.`
  ) : (
    <>
      Filters
      {addTest && (
        <>
          {' '}
          and unit test &quot;<b>{ruleDetails.tests[ruleDetails.tests.length - 1].name}</b>
          &quot;
        </>
      )}{' '}
      have been successfully saved to your detection.
    </>
  );
};

const RuleFilterSidesheetFormAlertV3: React.FC<RuleFilterSidesheetFormAlertProps> = ({
  ruleDetails,
  isValid,
  error,
  isSuccess = false,
  addTest,
  additionalSuccessMessageData,
}) => {
  return (
    <Alert
      variant={isSuccess ? 'success' : 'error'}
      actions={
        error && (
          <LinkButtonV3
            variant="destructive"
            external
            to={urls.build.detections.rules.details(ruleDetails.id)}
            aria-label="View Detection"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexWrap: 'nowrap',
              whiteSpace: 'nowrap',
            }}
          >
            View Detection
          </LinkButtonV3>
        )
      }
      title={
        <AlertTitle
          ruleDetails={ruleDetails}
          isValid={isValid}
          error={error}
          addTest={addTest}
          additionalSuccessMessageData={additionalSuccessMessageData}
        />
      }
    />
  );
};

export default RuleFilterSidesheetFormAlertV3;
